import { Component, OnInit, ViewEncapsulation, inject } from "@angular/core";
import { ProcessedDocumentsService } from "@app/document-intelligence/services/processed-documents-service";
import { TabModel } from "@models/generic/TabModels/TabModel";
import { ProcessedDocumentOverviewModel } from "@app/document-intelligence/models/ProcessedDocumentOverviewModel";
import { nameof } from "@methods/jeffs-toolkit";
import { GenericPaginationListConfig } from "@app/shared/models/GenericPaginationList/GenericPaginationListConfig";
import { TabService } from "@services/tabs/TabService";
import { Pages } from "@constants/pages/Pages";
import { FormatType } from "@enums/FormatType";
import { CerrixDialogService } from "@app/shared/organisms/cerrix-dialog/cerrix-dialog-service.component";
import { firstValueFrom } from "rxjs";
import { CerrixHeaderComponent } from "@app/shared/molecules/cerrix-header/cerrix-header.component";
import { CerrixFilteredGenericListManagerComponent } from "@app/shared/organisms/cerrix-filtered-generic-list-manager/cerrix-filtered-generic-list-manager.component";
import { GenerateAiRisksDialogComponent } from "@app/risk/shared/dialog/generate-ai-risks-dialog/generate-ai-risks-dialog.component";

@Component({
    selector: "risk-generate-ai-overview",
    templateUrl: "./risk-generate-ai-overview.component.html",
    styleUrls: ["./risk-generate-ai-overview.component.scss"],
    imports: [CerrixHeaderComponent, CerrixFilteredGenericListManagerComponent, GenerateAiRisksDialogComponent],
    encapsulation: ViewEncapsulation.None,
})
export class RiskGenerateAiOverviewComponent implements OnInit {
    private _tabService = inject(TabService);
    private _pages = inject(Pages);
    private _dialogService = inject(CerrixDialogService);

    private _processedDocumentsService = inject(ProcessedDocumentsService);
    private tab?: TabModel;
    title = "AI Document Extraction";
    subtitle = "Upload documents and extract risks and controls using AI";
    dialogName = "generateAiRisks";
    data?: ProcessedDocumentOverviewModel[];
    config: GenericPaginationListConfig;

    async ngOnInit(): Promise<void> {
        const canAccess = await firstValueFrom(this._processedDocumentsService.canAccess());
        if (!canAccess) {
            this.tab.close(false);
            return;
        }
        await this.loadData();
        this.createConfig();

        this.tab.showLoader = false;
    }

    private async loadData() {
        this.data = await firstValueFrom(this._processedDocumentsService.getNewOverview());
    }

    private createConfig() {
        if (this.config) {
            return;
        }

        this.config = <GenericPaginationListConfig>{
            idProp: nameof<ProcessedDocumentOverviewModel>((x) => x.id),
            sortProp: nameof<ProcessedDocumentOverviewModel>((x) => x.dateCreated),
            overviewSortBy: nameof<ProcessedDocumentOverviewModel>((x) => x.dateCreated),
            overviewSortAsc: false,
            uiid: "risk-generate-ai-overview",
            isSortableByColumn: true,

            allowEdit: true,

            fields: [
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.title),
                    prettyName: "Name",
                    headerCssClass: "th-main",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.status),
                    prettyName: "Status",
                    headerCssClass: "th-text",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.owner),
                    prettyName: "Owner",
                    headerCssClass: "th-text",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.createdRisks),
                    prettyName: "Created risks",
                    headerCssClass: "th-number",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.createdControls),
                    prettyName: "Created controls",
                    headerCssClass: "th-number",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.dateCreated),
                    prettyName: "Date created",
                    formatType: FormatType.DateFormat,
                    headerCssClass: "th-date",
                },
                {
                    fieldName: nameof<ProcessedDocumentOverviewModel>((x) => x.dateCompleted),
                    prettyName: "Date completed",
                    formatType: FormatType.DateFormat,
                    headerCssClass: "th-date",
                },
            ],

            data: this.data,
            editOverride: (row) => {
                this._tabService.generateTab(
                    this._pages.NewRiskAi,
                    row.id,
                    "Risk AI Generation Detail",
                );
            },
            setAdditionalColumns: (row) => {},
        };
    }

    protected openAddPopup() {
        this._dialogService.openDialog(this.dialogName);
    }
}
